import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import * as UsersService from '../services/UsersService';
import {User, UsersFilter} from '../types';
import {notifyError, notifySuccess} from '../../../components/PlatformNotification';

export const usersKeys = {
    all: ['users'] as const,
    lists: () => [...usersKeys.all, 'list'] as const,
    list: (filters: UsersFilter) => [...usersKeys.lists(), {filters}] as const,
    details: () => [...usersKeys.all, 'detail'] as const,
    detail: (username: string) => [...usersKeys.details(), username] as const,
};

// Fixed by the module and sent as users-api's `limit` (clamped there to 1-60).
// There is no page-size control: the footer states what is loaded (checklist §9).
export const PAGE_SIZE = 50;

// Set deliberately: the default 0 refetches every loaded page on each mount, so
// coming back from a person to a list paged three deep would refetch all three.
const STALE_TIME = 30_000;

export const useUsersInfiniteQuery = (filters: UsersFilter = {}) => {
    return useInfiniteQuery({
        queryKey: usersKeys.list(filters),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            UsersService.fetchRecords(filters, PAGE_SIZE, pageParam),
        // undefined, never null, when done: null keeps react-query asking.
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        initialPageParam: null as string | null,
        staleTime: STALE_TIME,
    });
};

// One person, by username, straight from users-api. The edit page and the edit
// drawer read this, never the list: under token paging a person on a page not
// loaded yet is not in the list at all.
export const useUserQuery = (username: string) => {
    return useQuery({
        queryKey: usersKeys.detail(username),
        queryFn: () => UsersService.fetch(username),
        enabled: !!username,
        staleTime: STALE_TIME,
    });
};

export const useCreateUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: UsersService.add,
        onSuccess: () => {
            notifySuccess('User created');
            queryClient.invalidateQueries({queryKey: usersKeys.lists()});
        },
        onError: notifyError,
    });
};

export const useUpdateUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({username, record}: { username: string; record: User }) =>
            UsersService.update(username, record),
        onSuccess: (_, {username}) => {
            notifySuccess('User updated');
            queryClient.invalidateQueries({queryKey: usersKeys.lists()});
            queryClient.invalidateQueries({queryKey: usersKeys.detail(username)});
        },
        onError: notifyError,
    });
};

// The person is gone, so the lists refetch and the detail entry is REMOVED, not
// refetched: refetching it would ask users-api for someone who no longer exists.
export const useDeleteUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: UsersService.deleteRecord,
        onSuccess: (_, username) => {
            queryClient.invalidateQueries({queryKey: usersKeys.lists()});
            queryClient.removeQueries({queryKey: usersKeys.detail(username)});
        },
    });
};
